<script setup lang="ts">
const {
  year,
  years,
} = defineProps<{
  year: number
  years: { value: number; text: string }[]
}>()

const emit = defineEmits([
  'update:year',
  'openMonthOverlay',
  'closeYearOverlay',
])

const yearSlideIndex = ref(0)
const visibleYearsRange = ref([])
const MAX_YEARS_PER_SLIDE = 24

function getSelectedYearIndex(): number {
  return years.map(year => year.value).indexOf(year)
}

function getVisibleYearsRange() {
  const yearsLength = years.length

  // Start
  visibleYearsRange.value.push(yearSlideIndex.value * MAX_YEARS_PER_SLIDE)

  // End
  const maxVisibleYearIndex = visibleYearsRange.value[0] + MAX_YEARS_PER_SLIDE - 1

  visibleYearsRange.value.push(
    maxVisibleYearIndex > yearsLength ? yearsLength - 1 : maxVisibleYearIndex,
  )
}

onMounted(() => {
  yearSlideIndex.value = Math.floor(getSelectedYearIndex() / MAX_YEARS_PER_SLIDE)

  getVisibleYearsRange()
})

function onYearsPrev() {
  if (yearSlideIndex.value === 0)
    return

  const yearsLength = years.length

  yearSlideIndex.value = yearSlideIndex.value - 1

  visibleYearsRange.value.splice(0, 0, yearSlideIndex.value * MAX_YEARS_PER_SLIDE)

  const maxYearsIndex = visibleYearsRange.value[0] + MAX_YEARS_PER_SLIDE - 1

  visibleYearsRange.value.splice(1, 0, maxYearsIndex > yearsLength ? yearsLength - 1 : maxYearsIndex)
}

function onYearsNext() {
  const yearsLength = years.length

  if (yearSlideIndex.value === Math.floor(yearsLength / MAX_YEARS_PER_SLIDE))
    return

  yearSlideIndex.value = yearSlideIndex.value + 1

  visibleYearsRange.value.splice(0, 0, yearSlideIndex.value * MAX_YEARS_PER_SLIDE)

  const maxYearsIndex = visibleYearsRange.value[0] + MAX_YEARS_PER_SLIDE - 1

  visibleYearsRange.value.splice(1, 0, maxYearsIndex > yearsLength ? yearsLength - 1 : maxYearsIndex)
}

function getYearTitle(): string {
  return `${years[visibleYearsRange.value[0]]?.text} - ${years[visibleYearsRange.value[1]]?.text}`
}

function selectYear(year) {
  emit('update:year', year)
  emit('closeYearOverlay')
  emit('openMonthOverlay')
}
</script>

<template>
  <div class="dp__overlay p-5 rounded-xl px-4">
    <div class="dp__overlay_container flex">
      <div class="flex justify-between items-center capitalize font-semibold">
        <AppIcon
          class="
            hover:bg-primary hover:bg-opacity-15
            cursor-pointer
            px-5
            py-4.5
            rounded-xl
            text-primary
          "
          icon="angle-left"
          type="far"
          @click="onYearsPrev"
        />
        <span
          class="
            hover:bg-primary hover:bg-opacity-15
            py-2.5
            px-4
            cursor-pointer
            rounded-xl
            flex
          "
          @click="emit('closeYearOverlay')"
        >
          {{ getYearTitle() }}

          <AppIcon class="text-primary ml-2" icon="angle-down" type="far" />
        </span>
        <AppIcon
          class="
            hover:bg-primary hover:bg-opacity-15
            cursor-pointer
            px-5
            py-4.5
            rounded-xl
            text-primary
          "
          icon="angle-right"
          type="far"
          @click="onYearsNext"
        />
      </div>

      <div class="flex flex-wrap gap-y-2.5 font-medium">
        <div
          v-for="(yearItem, index) in years.slice(visibleYearsRange?.[0], visibleYearsRange?.[1] + 1)"
          :key="`datepicker-year-${index}`"
          class="basis-1/4 flex-grow-0 flex-shrink-0 flex justify-center"
        >
          <span
            class="
              cursor-pointer
              px-2.5
              py-2
              rounded-lg
              hover:bg-primary hover:bg-opacity-15
            "
            :class="{ 'border border-primary ': year === yearItem.value }"
            @click="selectYear(yearItem.value)"
          >
            {{ yearItem.text }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>
